import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient, { getAuthHeaders, withApiPath } from '../utils/apiClient';
import Button from './ui/Button';
import Surface from './ui/Surface';
import styles from './LoginPage.module.css';
import { AUTH_SCOPE_CHANGED_EVENT } from './loadContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    showPassword: false,
    rememberMe: false
  });
  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const togglePasswordVisibility = () => {
    setFormData((prev) => ({
      ...prev,
      showPassword: !prev.showPassword
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username) {
      newErrors.username = 'Username is required';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      const response = await apiClient.post(withApiPath('/token/'), {
        username: formData.username,
        password: formData.password
      });

      if (response.status === 200) {
        const token = response.data.access;
        const storage = formData.rememberMe ? localStorage : sessionStorage;
        storage.setItem('token', token);

        try {
          const accountResponse = await apiClient.get(withApiPath('/users/me/'), {
            headers: getAuthHeaders(token)
          });

          if (accountResponse.status === 200) {
            const user = accountResponse.data;
            const account = accountResponse.data.account;
            storage.setItem('userId', user.id);
            if (account && account.id) {
              storage.setItem('accountId', account.id);
            }
            window.dispatchEvent(new Event(AUTH_SCOPE_CHANGED_EVENT));
            navigate('/dashboard');
          }
        } catch (accountError) {
          console.error('Error fetching user and account info:', accountError);
          setErrors({ form: 'Unable to fetch your workspace profile. Please try again.' });
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({
        form: error.response?.data?.detail || 'We could not authenticate you. Please review your credentials.'
      });
    }
  };

  return (
    <div className={styles.page}>
      <Surface tone="glass" padding="lg" className={styles.card}>
        <Link to="/" className={styles.brandLink}>
          ← Back to homepage
        </Link>
        <div className={styles.header}>
          <span>FreightLogic access</span>
          <h1>Welcome back</h1>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>
            Log in to resume orchestrating premium freight experiences.
          </p>
        </div>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label htmlFor="username">Email or username</label>
            <div className={styles.inputShell}>
              <input
                id="username"
                name="username"
                type="text"
                placeholder="weston@freightlogic.com"
                value={formData.username}
                onChange={handleChange}
              />
            </div>
            {errors.username && <span className={styles.error}>{errors.username}</span>}
          </div>
          <div className={styles.field}>
            <label htmlFor="password">Password</label>
            <div className={styles.inputShell}>
              <input
                id="password"
                name="password"
                type={formData.showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
              />
              <span className={styles.toggle} onClick={togglePasswordVisibility}>
                {formData.showPassword ? 'Hide' : 'Show'}
              </span>
            </div>
            {errors.password && <span className={styles.error}>{errors.password}</span>}
          </div>
          <div className={styles.controls}>
            <label htmlFor="rememberMe">
              <input
                id="rememberMe"
                name="rememberMe"
                type="checkbox"
                checked={formData.rememberMe}
                onChange={handleChange}
              />
              Remember me
            </label>
            <Link to="/forgot-password">Forgot password?</Link>
          </div>
          <Button type="submit" variant="primary" size="lg" className={styles.submit}>
            Sign in
          </Button>
          {errors.form && <span className={styles.error}>{errors.form}</span>}
        </form>
        <div className={styles.footerLinks}>
          <Link to="/privacy">Privacy</Link>
          <Link to="/support">Support</Link>
        </div>
      </Surface>
    </div>
  );
};

export default LoginPage;
